import { BigNumber } from '@ethersproject/bignumber';
import { useCallback, useEffect, useState } from 'react';
import { convertToUSD } from '@/common/currency';
import useMainStore from '@/store/mainStore';

/**
 * Get current connected user balance
 * Will fetch current ETH balance based on the current network
 * @returns The balance, in both ETH and USD, and the ERC20 balance, key by ${networkId}-${walletAddress}
 */
export function useUserBalance() {
  // key by ${networkId}-${walletAddress}
  const [balance, setBalance] = useState<{
    [key: string]: {
      balance: BigNumber;
      networkId: number;
      walletAddress: string;
      usdValue?: number;
    };
  }>();
  const [isLoading, setIsLoading] = useState(false);
  const address = useMainStore((state) => state.walletAddress);
  const connectedNetwork = useMainStore((state) => state.connectedNetwork);

  const fetchBalance = useCallback(async () => {
    setIsLoading(true);
    const provider = window.ManifoldEthereumProvider.provider();
    if (!provider || !address || !connectedNetwork) {
      return;
    }

    try {
      const walletBalance = await provider.getBalance(address);

      let usdValue: number | undefined;
      // Convert balance to USD
      try {
        usdValue = await convertToUSD(connectedNetwork, walletBalance);
      } catch (error) {
        console.warn('Error converting balance to USD');
        console.warn(error);
      }
      setBalance({
        [`${connectedNetwork}-${address}`]: {
          balance: walletBalance,
          networkId: connectedNetwork,
          walletAddress: address,
          usdValue,
        },
      });
    } catch (error) {
      console.error('Error fetching ETH balance');
      console.error(error);
    }

    setIsLoading(false);
  }, [address, connectedNetwork]);

  useEffect(() => {
    fetchBalance();
  }, [address, connectedNetwork, fetchBalance]);

  return { balance, isLoading, fetchBalance };
}
